import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import type { ComboboxLabelValue } from "@/components/ui/combobox";
import { useAuth } from "@/features/auth";
import {
  useProjectsQuery,
  useWorkspaceBySlugQuery,
} from "@/features/containers/api/containers-hooks";
import {
  ContainerCombobox,
  renderContainerComboboxItemWithOptionalSeparator,
} from "@/features/containers/components/container-combobox";
import { useGoToProject } from "@/features/projects/projects.hooks";

interface ProjectComboboxProps {
  /**
   * CR-144: explicit context for pages without workspace/project route params
   * (Settings). Passing `workspaceSlug` switches to explicit mode: `projectSlug`
   * is then never read from the route, and an unknown `projectSlug` renders
   * nothing once the projects have loaded. Omitted, the combobox reads the
   * route params as before.
   */
  workspaceSlug?: string;
  projectSlug?: string;
}

export const ProjectCombobox = (props: ProjectComboboxProps) => {
  const { user } = useAuth();
  const params = useParams({ strict: false });
  const isExplicit = props.workspaceSlug !== undefined;
  const slug = isExplicit ? props.workspaceSlug : params.slug;
  const projectSlug = isExplicit ? props.projectSlug : params.projectSlug;

  const { data: workspace } = useWorkspaceBySlugQuery(slug);
  const { goToProject } = useGoToProject(slug);

  const { data: projects, isLoading } = useProjectsQuery(user.id, workspace?.id);

  const projectsForCombobox: ComboboxLabelValue[] = useMemo(
    () => [
      ...(projectSlug
        ? ([
            {
              label: "All projects",
              value: "/",
            },
          ] as const)
        : []),
      ...(projects?.map((project) => ({
        label: project.name || project.slug,
        value: project.slug,
      })) || []),
    ],
    [projectSlug, projects],
  );

  const selectedProject = useMemo(() => {
    if (!projectSlug) return null;
    return projectsForCombobox.find(({ value }) => value === projectSlug) || null;
  }, [projectSlug, projectsForCombobox]);

  if (!slug || !workspace) {
    return null;
  }
  // CR-144: an explicit project slug comes from the URL of a page that cannot
  // guarantee it exists (a stale or hand-edited Settings link): once the
  // projects have loaded without it, show nothing rather than a wrong project.
  if (isExplicit && projectSlug && projects && !selectedProject) {
    return null;
  }
  return (
    <ContainerCombobox<ComboboxLabelValue>
      items={projectsForCombobox}
      selectedValue={selectedProject}
      isLoading={isLoading}
      placeholder="Select a project"
      emptyText="No projects found."
      shrinkToFit={!selectedProject?.label ? 130 : Math.min(selectedProject.label.length * 15, 130)} // TODO: solve with css
      onSelect={(next) => {
        if (next.value) void goToProject(next.value);
      }}
      renderItem={(item) =>
        item.value === "/"
          ? renderContainerComboboxItemWithOptionalSeparator({
              item,
              shouldRenderSeparatorAfter: true,
            })
          : renderContainerComboboxItemWithOptionalSeparator({
              item,
              shouldRenderSeparatorAfter: false,
            })
      }
    />
  );
};
